import type { ReactNode } from 'react';
import { SearchIcon } from '@/components/icons';

/**
 * Small layout pieces shared by the resource pages. Each page keeps its own
 * CSS; `classPrefix` selects it (e.g. "certs" -> `certs-header`, `certs-error`).
 */

export interface PageHeaderProps {
  classPrefix: string;
  title: string;
  /** Count text, e.g. "3 nodes" */
  count?: string;
  /** Actions on the right (wrapped in `${prefix}-header-actions` when `wrapActions`) */
  actions?: ReactNode;
  wrapActions?: boolean;
}

/**
 * Page title row with item count and action buttons
 */
export function PageHeader({ classPrefix, title, count, actions, wrapActions = false }: PageHeaderProps) {
  return (
    <div className={`${classPrefix}-header`}>
      <div className={`${classPrefix}-header-left`}>
        <h1 className={`${classPrefix}-title`}>{title}</h1>
        {count !== undefined && <span className={`${classPrefix}-count`}>{count}</span>}
      </div>
      {actions &&
        (wrapActions ? <div className={`${classPrefix}-header-actions`}>{actions}</div> : actions)}
    </div>
  );
}

export interface ErrorAlertProps {
  classPrefix: string;
  message: string | null | undefined;
  /** Shows a Retry button */
  onRetry?: () => void;
}

/**
 * Page-level error banner
 */
export function ErrorAlert({ classPrefix, message, onRetry }: ErrorAlertProps) {
  if (!message) return null;

  return (
    <div className={`${classPrefix}-error`} role="alert">
      <span className={`${classPrefix}-error-icon`}>!</span>
      <span>{message}</span>
      {onRetry && (
        <button type="button" className={`${classPrefix}-error-retry`} onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export interface SearchFieldProps {
  classPrefix: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

/**
 * Search input with icon used in filter bars
 */
export function SearchField({ classPrefix, value, onChange, placeholder }: SearchFieldProps) {
  return (
    <div className={`${classPrefix}-search`}>
      <SearchIcon />
      <input
        type="text"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${classPrefix}-search-input`}
      />
    </div>
  );
}

export interface ClearFiltersButtonProps {
  classPrefix: string;
  onClick: () => void;
}

/**
 * "Clear filters" link-button shown when any filter is active
 */
export function ClearFiltersButton({ classPrefix, onClick }: ClearFiltersButtonProps) {
  return (
    <button type="button" className={`${classPrefix}-clear-filters`} onClick={onClick}>
      Clear filters
    </button>
  );
}
